import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { advisorAdminApi, advisorAdminKeys } from '../api/api';

const preRegKeys = {
  all: [...advisorAdminKeys.all, 'preRegisteredFeatureFlags'] as const,
  list: () => [...preRegKeys.all, 'list'] as const,
};

export function usePreRegisteredFeatureFlagsList() {
  return useQuery({
    queryKey: preRegKeys.list(),
    queryFn: () => advisorAdminApi.fetchPreRegisteredFeatureFlags(),
  });
}

export function useAddPreRegisteredFeatureFlag() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { email: string; featureFlags: FeatureFlags }) =>
      advisorAdminApi.addPreRegisteredFeatureFlag(data),
    onSuccess: () => {
      toast.success('Email pre-registered successfully');
      queryClient.invalidateQueries({ queryKey: preRegKeys.list() });
    },
  });

  return {
    addPreRegisteredFeatureFlag: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useAddPreRegisteredFeatureFlagsBulk() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { emails: string[]; featureFlags: FeatureFlags }) =>
      advisorAdminApi.addPreRegisteredFeatureFlagsBulk(data),
    onSuccess: (data) => {
      toast.success(`${data.inserted} email(s) pre-registered, ${data.skipped} skipped (duplicates)`);
      queryClient.invalidateQueries({ queryKey: preRegKeys.list() });
    },
  });

  return {
    addBulk: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useSearchPreRegisteredFeatureFlag() {
  const mutation = useMutation({
    mutationFn: (email: string) =>
      advisorAdminApi.searchPreRegisteredFeatureFlag({ email }),
  });

  return {
    search: mutation.mutate,
    data: mutation.data ?? null,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useDeletePreRegisteredFeatureFlag() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) =>
      advisorAdminApi.deletePreRegisteredFeatureFlag(id),
    onSuccess: () => {
      toast.success('Pre-registered email removed');
      queryClient.invalidateQueries({ queryKey: preRegKeys.list() });
    },
  });

  return {
    deletePreRegisteredFeatureFlag: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
